<template>
  <div class="cover-selector">
    <div class="cover-selector__header">
      <span class="cover-selector__title">
        {{ _t('package.select_cover') }}
      </span>

      <Icon
        name="close-circle"
        class="cover-selector__close"
        @click="emit('done')"
      ></Icon>
    </div>

    <PackageCoverCard
      :package="showingPackage"
      size="normal"
      :show-menu="false"
      :show-share="false"
    />

    <Button
      class="cover-selector__use-btn"
      :label="_t('common.use')"
      @click="emit('update', selectedStyle), emit('done')"
    />

    <TabView
      v-model:activeIndex="tabIndex"
      class="cover-selector__tabs"
    >
      <TabPanel :header="_t('common.image')">
        <div class="cover-selector__tab-content">
          <div class="cover-selector__toolbar">
            <span class="cover-selector__search-icon pi pi-search"></span>
            <input
              v-model="searchValue"
              :placeholder="_t('common.search')"
              class="cover-selector__search-input"
            />
            <Button
              class="cover-selector__help-select-btn"
              :label="_t('common.help_select')"
              icon="pi pi-compass"
              scene="secondary"
              :loading="helpSelectLoading"
              size="small"
              @click="onHelpSelect"
            />
          </div>
          <div class="cover-selector__image-grid">
            <img
              v-if="
                props.package.previewImgs && props.package.previewImgs.length
              "
              :src="`${_global.assetUrl(props.package.previewImgs[0])}`"
              class="cover-selector__image"
              :class="{
                'cover-selector__image--selected':
                  selectedCoverId === props.package.previewImgs[0],
              }"
            />
            <img
              v-for="cover in filteredCovers"
              :key="cover.id"
              :src="`${_global.assetUrl(cover.ossKeys.m)}`"
              class="cover-selector__image"
              :class="{
                'cover-selector__image--selected': selectedCoverId === cover.id,
              }"
              @click="onImageSelect(cover)"
            />
          </div>
        </div>
      </TabPanel>
      <TabPanel :header="_t('common.emoji')">
        <div class="cover-selector__tab-content">
          <div class="cover-selector__section-title">
            {{ _t('common.color') }}
          </div>
          <div class="cover-selector__colors">
            <div
              v-for="item in PackageColor"
              :key="item"
              class="cover-selector__color"
              :style="{
                backgroundColor: `var(--${item}-300)`,
              }"
              @click="selectedStyle.themeColor = item"
            >
              <Icon
                v-if="selectedStyle.themeColor === item"
                name="color-selected"
                class="cover-selector__color-selected"
              />
            </div>
          </div>
          <div class="cover-selector__section-title">
            {{ _t('common.emoji') }}
          </div>
          <div class="cover-selector__emoji-container">
            <div
              v-for="category in emojiTable"
              :key="category.label"
            >
              <div class="cover-selector__emoji-grid">
                <span
                  v-for="emoji in category.emojis"
                  :key="emoji"
                  class="cover-selector__emoji"
                  :class="{
                    'cover-selector__emoji--selected':
                      selectedStyle.icon.content === emoji,
                  }"
                  @click="onEmojiSelect(emoji)"
                >
                  {{ emoji }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </TabPanel>
    </TabView>
  </div>
</template>

<script lang="ts" setup>
import { PackageColor } from '@/api/package-source'
import { ref, computed, onMounted, watch, nextTick } from 'vue'

import type { PackageBasic, PackageStyle } from '@/api/package-source'
import PackageCoverCard from '@/components/business/package/PackageCoverCard.vue'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import emojiTable from './emoji.json'
import {
  type PackageCoverItem,
  type PackageCoverMetadata,
  generateAndSavePackageNameTags,
} from '@/api/package-source'
import { useCommonStore } from '@/stores/common-store'

const props = defineProps<{
  package: PackageBasic
}>()

const emit = defineEmits<{
  done: []
  update: [PackageStyle]
}>()

const store = useCommonStore()

const selectedStyle = ref<PackageStyle>({ ...props.package.style })
const showingPackage = computed<PackageBasic>(() => {
  return { ...props.package, style: selectedStyle.value as PackageStyle }
})

const packageCoverMetadata = ref<PackageCoverMetadata | null>(null)
const selectedCoverId = computed<string>(() => {
  if (
    selectedStyle.value.icon?.type === 'imgv2' &&
    selectedStyle.value.icon?.content
  ) {
    const url = selectedStyle.value.icon.content
    const match = url.match(/\/(\d+)\.\w+\.(jpg|jpeg|png|webp)$/)
    if (match) {
      return match[1]
    }
  }
  return ''
})

const searchValue = ref('')
const filteredCovers = computed(() => {
  if (!searchValue.value) {
    return packageCoverMetadata.value?.covers || []
  }
  return (
    packageCoverMetadata.value?.covers.filter(
      cover =>
        cover.tags.some(tag => tag.includes(searchValue.value)) ||
        cover.subjects.some(subject =>
          subject.title.includes(searchValue.value)
        )
    ) || []
  )
})

const tabIndex = ref(0)
const helpSelectLoading = ref(false)
const apiRequestPending = ref(false) // 跟踪API请求状态

// 滚动到选中的图片位置
function scrollToSelectedImage(identifier: string) {
  // 使用 nextTick 确保 DOM 更新完成
  nextTick(() => {
    // 尝试查找包含指定标识符的图片元素
    let imageElement = document.querySelector(
      `.cover-selector__image[src*="${identifier}"]`
    ) as HTMLElement

    // 如果没找到，尝试更精确的匹配
    if (!imageElement) {
      imageElement = document.querySelector(
        `.cover-selector__image[src*="${identifier}."]`
      ) as HTMLElement
    }

    if (imageElement) {
      imageElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      })
    }
  })
}

function onImageSelect(cover: PackageCoverItem) {
  // 如果正在loading，取消loading状态
  if (helpSelectLoading.value) {
    helpSelectLoading.value = false
  }

  selectedStyle.value.icon = {
    type: 'imgv2',
    content: _global.assetUrl(cover.ossKeys.xxl),
  }

  // 滚动到选中的图片位置
  scrollToSelectedImage(cover.id)
}

function onEmojiSelect(emoji: string) {
  // 如果正在loading，取消loading状态
  if (helpSelectLoading.value) {
    helpSelectLoading.value = false
  }

  selectedStyle.value.icon = {
    type: 'emoji',
    content: emoji,
  }
}

// 随机选择图片的辅助函数
function selectRandomImageByTags(tags: string[]): PackageCoverItem | null {
  if (!packageCoverMetadata.value?.covers?.length) return null

  // 找出tags或subject.title与packageNameTags有交集的图片
  const matchedCovers = packageCoverMetadata.value.covers.filter(cover => {
    // 排除当前已选中的图片
    if (selectedCoverId.value === cover.id) return false

    const coverTags = cover.tags || []
    const subjectTitles = cover.subjects?.map(s => s.title) || []
    const allCoverKeywords = [...coverTags, ...subjectTitles]

    return tags.some(tag =>
      allCoverKeywords.some(
        keyword =>
          keyword.toLowerCase().includes(tag.toLowerCase()) ||
          tag.toLowerCase().includes(keyword.toLowerCase())
      )
    )
  })

  if (matchedCovers.length === 0) return null

  // 随机选择一个
  const randomIndex = Math.floor(Math.random() * matchedCovers.length)

  return matchedCovers[randomIndex]
}

// 从所有图片中随机选择一张
function selectRandomImageFromAll(): PackageCoverItem | null {
  if (!packageCoverMetadata.value?.covers?.length) return null

  // 过滤掉当前已选中的图片
  const availableCovers = packageCoverMetadata.value.covers.filter(
    cover => selectedCoverId.value !== cover.id
  )

  if (availableCovers.length === 0) return null

  const randomIndex = Math.floor(Math.random() * availableCovers.length)
  return availableCovers[randomIndex]
}

// 智能选择图片：优先根据标签匹配，无匹配时随机选择
function smartSelectImage(tags?: string[]): PackageCoverItem | null {
  if (tags && tags.length > 0) {
    // 先尝试根据标签匹配
    const selectedCover = selectRandomImageByTags(tags)
    if (selectedCover) {
      return selectedCover
    }
  }

  // 没有标签或无匹配，随机选择一张
  return selectRandomImageFromAll()
}

// 帮我选功能
async function onHelpSelect() {
  if (helpSelectLoading.value) return
  helpSelectLoading.value = true
  if (apiRequestPending.value) return

  helpSelectLoading.value = true

  let packageNameTags = selectedStyle.value.packageNameTags
  const hasExistingTags = !!(packageNameTags && packageNameTags.length > 0)

  if (!hasExistingTags) {
    // 如果没有packageNameTags，先调用API生成
    apiRequestPending.value = true // 标记API请求开始
    try {
      const result = await generateAndSavePackageNameTags(
        props.package.id,
        props.package.name
      )

      if (result.code === 0 && result.data && result.data.length > 0) {
        // 更新packageNameTags
        selectedStyle.value.packageNameTags = result.data
        packageNameTags = result.data

        // 只更新 packageNameTags，保持其他样式不变
        emit('update', {
          ...props.package.style,
          packageNameTags: result.data,
        })
      }
    } catch (error) {
      console.error('生成包名标签失败:', error)
    } finally {
      apiRequestPending.value = false // 标记API请求结束
    }
  }

  // 图片选择逻辑
  const selectedCover = smartSelectImage(packageNameTags)
  if (selectedCover) {
    onImageSelect(selectedCover)
  }

  helpSelectLoading.value = false
}

// 监听tab切换，如果正在loading则取消
watch(tabIndex, () => {
  if (helpSelectLoading.value) {
    helpSelectLoading.value = false
  }
})

onInit(async () => {
  packageCoverMetadata.value = await store.packageCoverMetadata
})

onMounted(() => {
  // primevue 有bug，如果直接改变 activeIndex，会导致 higlight 的样式错位
  if (props.package.style.icon?.type === 'emoji') {
    setTimeout(() => {
      tabIndex.value = 1
    }, 500)
  }
})
</script>

<style scoped>
.cover-selector {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 8px;
}

@media (min-width: 640px) {
  .cover-selector {
    gap: 16px;
  }
}

.cover-selector__header {
  text-align: center;
  position: relative;
  width: 100%;
}

.cover-selector__title {
  font-size: 19px;
  line-height: 24px;
  font-weight: 600;
}

.cover-selector__close {
  cursor: pointer;
  position: absolute;
  right: 16px;
  top: 12px;
  width: 22px;
}

.cover-selector__use-btn {
  width: 100%;
}

.cover-selector__tabs {
  width: 100%;
}

.cover-selector__tab-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
}

.cover-selector__toolbar {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.cover-selector__search-icon {
  position: absolute;
  left: 12px;
  color: var(--text-color-secondary);
}

.cover-selector__search-input {
  flex: 1;
  height: 36px;
  padding-left: 32px;
  padding-right: 12px;
  border-radius: 8px;
  border: 1px solid var(--ld-border);
  background-color: var(--surface-ground);
  color: var(--text-color);
  transition: border-color 0.2s;
}

.cover-selector__search-input::placeholder {
  color: var(--text-color-secondary);
}

.cover-selector__search-input:focus {
  border-color: var(--primary-color);
}

.cover-selector__help-select-btn {
  flex-shrink: 0;
  height: 100%;
  white-space: nowrap;
}

.cover-selector__image-grid {
  display: grid;
  gap: 4px;
  overflow: auto;
  justify-items: center;
  grid-template-columns: repeat(auto-fit, minmax(74px, 1fr));
}

.cover-selector__image {
  width: 74px;
  height: 74px;
  border-radius: 8px;
  cursor: pointer;
  border: 3px solid transparent;
}

.cover-selector__image--selected {
  border: 3px solid var(--primary-color);
}

.cover-selector__section-title {
  font-size: 15px;
  line-height: 1;
  color: var(--text-color-secondary);
  font-weight: 600;
}

.cover-selector__colors {
  display: flex;
  justify-content: space-between;
  gap: 2px;
  width: 100%;
}

.cover-selector__color {
  width: 20px;
  height: 20px;
  border: 1px solid white;
  border-radius: 4px;
  position: relative;
  cursor: pointer;
  flex-shrink: 0;
}

.cover-selector__color-selected {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 13px;
}

.cover-selector__emoji-container {
  width: 100%;
  overflow: auto;
}

.cover-selector__emoji-grid {
  display: grid;
  gap: 4px;
  grid-template-columns: repeat(auto-fit, minmax(35px, 1fr));
}

.cover-selector__emoji {
  font-size: 24px;
  cursor: pointer;
  border: 1px solid transparent;
  border-radius: 4px;
  text-align: center;
  line-height: 1;
}

.cover-selector__emoji--selected {
  border: 1px solid var(--primary-color);
}

:deep(.p-tabview .p-tabview-nav),
:deep(.p-tabview .p-tabview-nav li .p-tabview-nav-link),
:deep(
    .p-tabview
      .p-tabview-nav
      li:not(.p-highlight):not(.p-disabled):hover
      .p-tabview-nav-link
  ),
:deep(.p-tabview .p-tabview-panels) {
  background-color: transparent;
}

:deep(.p-tabview .p-tabview-panels) {
  padding-left: 0;
  padding-right: 0;
}

:deep(.p-tabview .p-tabview-panel) {
  height: 250px;
}
</style>
